import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { registerDirtySource } from '../utils/unsavedChanges';

/**
 * Φόρμα με «αποθηκευμένη» και «τρέχουσα» τιμή (12/10/2026): ξέρει τι άλλαξε, ώστε η αποθήκευση να
 * στέλνει ΜΟΝΟ αυτό (PATCH με τη διαφορά — όχι ολόκληρη η φόρμα πάνω σε ό,τι άλλαξε στο μεταξύ άλλη
 * σελίδα) και η μπάρα «Έχεις αλλαγές…» να φαίνεται μόνο όταν υπάρχει κάτι να χαθεί.
 * Με `guardId` δηλώνεται και στο utils/unsavedChanges (ερώτηση πριν από αλλαγή καρτέλας / κλείσιμο).
 */

/** Ίδια τιμή για τη φόρμα: πρωτογενή με Object.is, πίνακες/αντικείμενα με το περιεχόμενο. */
export function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

export interface DirtyForm<T> {
  /** Τι δείχνει η φόρμα τώρα. */
  values: T;
  /** Τι είναι αποθηκευμένο (η βάση της σύγκρισης). */
  saved: T;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  setValues: (update: (prev: T) => T) => void;
  dirty: boolean;
  changedKeys: Array<keyof T>;
  /** Μόνο τα πεδία που άλλαξαν, με την τρέχουσα τιμή τους — το σώμα του PATCH. */
  changes: Partial<T>;
  /** Χωρίς όρισμα: πίσω στα αποθηκευμένα. Με όρισμα: νέα δεδομένα από τον server (αποθηκευμένα = τρέχοντα). */
  reset: (next?: T) => void;
  /** Ό,τι δείχνει η φόρμα αποθηκεύτηκε· `stored` = όσα επέστρεψε ο server (π.χ. κεφαλαία διεύθυνση). */
  markSaved: (stored?: Partial<T>) => void;
  /** Αυξάνεται σε κάθε reset — key για πεδία που δεν ελέγχονται από την τιμή (defaultValue). */
  version: number;
}

export function useDirtyForm<T extends object>(initial: T, options: { guardId?: string } = {}): DirtyForm<T> {
  const [state, setState] = useState(() => ({ saved: initial, values: initial, version: 0 }));
  const { saved, values, version } = state;

  const changedKeys = useMemo(() => {
    const keys = Array.from(new Set([...Object.keys(saved), ...Object.keys(values)])) as Array<keyof T>;
    return keys.filter(key => !sameValue(saved[key], values[key]));
  }, [saved, values]);
  const dirty = changedKeys.length > 0;

  const changes = useMemo(() => {
    const out: Partial<T> = {};
    changedKeys.forEach(key => { out[key] = values[key]; });
    return out;
  }, [changedKeys, values]);

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setState(prev => ({ ...prev, values: { ...prev.values, [key]: value } }));
  }, []);

  const setValues = useCallback((update: (prev: T) => T) => {
    setState(prev => ({ ...prev, values: update(prev.values) }));
  }, []);

  const reset = useCallback((next?: T) => {
    setState(prev => {
      const base = next ?? prev.saved;
      return { saved: base, values: base, version: prev.version + 1 };
    });
  }, []);

  const markSaved = useCallback((stored?: Partial<T>) => {
    setState(prev => {
      const next = stored ? { ...prev.values, ...stored } : prev.values;
      return { ...prev, saved: next, values: next };
    });
  }, []);

  // Η απάντηση στο «έχεις αλλαγές;» διαβάζεται από ref: το μητρώο ρωτά εκτός render.
  const dirtyRef = useRef(dirty);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  const { guardId } = options;
  useEffect(() => (guardId ? registerDirtySource(guardId, () => dirtyRef.current) : undefined), [guardId]);

  return { values, saved, setField, setValues, dirty, changedKeys, changes, reset, markSaved, version };
}
